import { useEffect, useMemo, useState } from "react";
import { Drawer, Modal, Select, message } from "antd";
import { Link } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useWorkspaceMembers, {
  getWorkspaceMemberDisplayName,
  getWorkspaceMemberEmail,
} from "../../hooks/workspaces/useWorkspaceMembers";
import { careService } from "../../services/careService";

const clean = (value) => String(value || "").trim();
const pad = (part) => String(part).padStart(2, "0");
const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
const localDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const timeKey = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;
const daysFromNow = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateKey(date);
};
const addMinutesToTime = (time = "09:00", minutes = 30) => {
  const [hour, minute] = String(time || "09:00").split(":").map((part) => Number(part));
  const date = new Date(2000, 0, 1, hour || 0, minute || 0);
  date.setMinutes(date.getMinutes() + minutes);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const parseDateKey = (value) => {
  const [year, month, day] = String(value || today()).split("-").map((part) => Number(part));
  return new Date(year || new Date().getFullYear(), (month || 1) - 1, day || 1);
};
const dateKeyFor = (value) => (typeof value === "string" ? value : localDateKey(value));
const isPastDateKey = (dateKey) => String(dateKey || "") < today();
const futureStartFor = (dateKey = today()) => {
  const safeDateKey = isPastDateKey(dateKey) ? today() : dateKey;
  if (safeDateKey !== today()) {
    return { date: safeDateKey, time: "09:00" };
  }
  const next = new Date(Date.now() + 15 * 60 * 1000);
  next.setMinutes(Math.ceil(next.getMinutes() / 15) * 15, 0, 0);
  return { date: localDateKey(next), time: timeKey(next) };
};
const monthLabel = (date) => date.toLocaleDateString([], { month: "long", year: "numeric" });
const addMonths = (date, delta) => new Date(date.getFullYear(), date.getMonth() + delta, 1);
const sameDay = (left, right) => localDateKey(left) === localDateKey(right);
const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const calendarDaysFor = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
};
const calendarRangeForMonth = (monthDate) => {
  const days = calendarDaysFor(monthDate);
  return {
    startDate: localDateKey(days[0]),
    endDate: localDateKey(days[days.length - 1]),
  };
};
const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};
const appointmentStartValue = (appointment = {}) => appointment.startTimestamp || appointment.startAt || "";
const appointmentEndValue = (appointment = {}) => appointment.endTimestamp || appointment.endAt || appointmentStartValue(appointment);
const overlapsDay = (appointment = {}, date) => {
  const start = new Date(appointmentStartValue(appointment));
  const end = new Date(appointmentEndValue(appointment));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  return start <= dayEnd && end >= dayStart;
};
const timeRangeLabel = (appointment = {}) => {
  const start = new Date(appointmentStartValue(appointment));
  const end = new Date(appointmentEndValue(appointment));
  if (Number.isNaN(start.getTime())) return "";
  const startLabel = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (Number.isNaN(end.getTime()) || end.getTime() === start.getTime()) return startLabel;
  return `${startLabel} - ${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
};
const formatTimestamp = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
const labelFor = (value) => String(value || "").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const statusClassFor = (status) => String(status || "SCHEDULED").trim().toLowerCase().replace(/_/g, "-");
const appointmentStatusValue = (appointment = {}) => String(appointment.status || "SCHEDULED").trim().toUpperCase();
const canConfirmAppointment = (appointment = {}) => appointmentStatusValue(appointment) === "SCHEDULED";
const canCompleteAppointment = (appointment = {}) => ["SCHEDULED", "CONFIRMED"].includes(appointmentStatusValue(appointment));
const canCancelAppointment = (appointment = {}) => !["CANCELLED", "CANCELED", "COMPLETED"].includes(appointmentStatusValue(appointment));
const statusIconFor = (status) => {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "COMPLETED" || normalized === "CONFIRMED") return "ti-circle-check";
  if (normalized === "CANCELLED" || normalized === "CANCELED") return "ti-ban";
  return "ti-calendar-clock";
};
const customerNameFor = (appointment = {}) =>
  clean(appointment.customerName) ||
  [appointment.firstName, appointment.lastName].map(clean).filter(Boolean).join(" ") ||
  "Customer";
const customerEmailFor = (appointment = {}) => clean(appointment.customerEmail) || clean(appointment.email);
const appointmentTitleFor = (appointment = {}) => clean(appointment.title) || `Visit with ${customerNameFor(appointment)}`;
const vehicleTitleFor = (vehicle = {}) =>
  clean(vehicle.title) ||
  [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].map(clean).filter(Boolean).join(" ") ||
  "Vehicle";
const normalizeAppointment = (appointment = {}, index = 0) => ({
  ...appointment,
  id: appointment.id || `appointment-${appointment.customerInquiryId || appointment.reference?.id || index}`,
  status: appointment.status || "SCHEDULED",
  title: appointmentTitleFor(appointment),
  customerName: customerNameFor(appointment),
  customerEmail: customerEmailFor(appointment),
  startTimestamp: appointmentStartValue(appointment),
  endTimestamp: appointmentEndValue(appointment),
  inquiryId: appointment.customerInquiryId || (appointment.reference?.type === "CUSTOMER_INQUIRY" ? appointment.reference?.id : "") || "",
  notes: clean(appointment.notes) || clean(appointment.customerMessage),
});
const defaultAppointmentForm = (date = today()) => {
  const start = futureStartFor(date);
  return {
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    vehicleId: "",
    hostUserId: "",
    hostName: "",
    hostEmail: "",
    title: "",
    location: "Dealer visit",
    notes: "",
    date: start.date,
    startTime: start.time,
    endTime: addMinutesToTime(start.time, 30),
    status: "SCHEDULED",
  };
};
const zonedIsoFromDateAndTime = (date, time) => {
  if (!date || !time) return null;
  const parsed = new Date(`${date}T${time}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};
const dateTimePartsFrom = (value) => {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return futureStartFor(today());
  return {
    date: localDateKey(parsed),
    time: timeKey(parsed),
  };
};
const appointmentFormFrom = (appointment = {}) => {
  const start = dateTimePartsFrom(appointmentStartValue(appointment));
  const end = dateTimePartsFrom(appointmentEndValue(appointment));
  return {
    customerName: customerNameFor(appointment) === "Customer" ? "" : customerNameFor(appointment),
    customerEmail: customerEmailFor(appointment),
    customerPhone: clean(appointment.customerPhone || appointment.phone),
    vehicleId: appointment.vehicleId || "",
    hostUserId: appointment.host?.id || appointment.hostId || "",
    hostName: clean(appointment.host?.name || appointment.hostName),
    hostEmail: clean(appointment.host?.email || appointment.hostEmail),
    title: clean(appointment.title),
    location: clean(appointment.location),
    notes: clean(appointment.notes),
    date: start.date,
    startTime: start.time,
    endTime: end.time,
    status: appointment.status || "SCHEDULED",
  };
};

export default function CareStaffSchedulesPage() {
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [appointments, setAppointments] = useState([]);
  const [rangeStart, setRangeStart] = useState(() => today());
  const [rangeEnd, setRangeEnd] = useState(() => daysFromNow(6));
  const [viewMode, setViewMode] = useState("calendar");
  const [calendarMonth, setCalendarMonth] = useState(() => parseDateKey(today()));
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(() => today());
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [detailDrawer, setDetailDrawer] = useState({ type: "", data: null, loading: false, error: "" });
  const [appointmentEditorOpen, setAppointmentEditorOpen] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState(() => defaultAppointmentForm());
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentSaving, setAppointmentSaving] = useState(false);
  const [appointmentActionLoading, setAppointmentActionLoading] = useState("");
  const [appointmentFormError, setAppointmentFormError] = useState("");
  const [calendarFullscreenOpen, setCalendarFullscreenOpen] = useState(false);
  const [calendarMoreDay, setCalendarMoreDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { users: workspaceUsers, loading: workspaceMembersLoading } = useWorkspaceMembers({
    enabled: appointmentEditorOpen,
  });

  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );
  const calendarFetchRange = useMemo(() => calendarRangeForMonth(calendarMonth), [calendarMonth]);
  const activeFetchRange = useMemo(() => (
    viewMode === "calendar"
      ? calendarFetchRange
      : { startDate: rangeStart, endDate: rangeEnd }
  ), [calendarFetchRange, rangeEnd, rangeStart, viewMode]);

  const loadData = async (overrides = {}) => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const params = {
      ...workspaceParams,
      startDate: overrides.rangeStart || activeFetchRange.startDate,
      endDate: overrides.rangeEnd || activeFetchRange.endDate,
      ...(statusFilter !== "ALL" ? { status: statusFilter } : {}),
    };
    try {
      const data = await careService.appointments.list(params);
      setAppointments(pageItems(data).map(normalizeAppointment));
    } catch (loadError) {
      setAppointments([]);
      setError(loadError?.response?.data?.message || loadError?.message || "Unable to load appointments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth, rangeEnd, rangeStart, selectedWorkspaceId, statusFilter, viewMode, workspaceListReady]);

  const filteredAppointments = useMemo(() => appointments.filter((appointment) => {
    if (statusFilter !== "ALL" && String(appointment.status || "").toUpperCase() !== statusFilter) return false;
    return true;
  }), [appointments, statusFilter]);
  const totalCount = filteredAppointments.length;
  const scheduledCount = filteredAppointments.filter((appointment) => String(appointment.status || "").toUpperCase() === "SCHEDULED").length;
  const confirmedCount = filteredAppointments.filter((appointment) => String(appointment.status || "").toUpperCase() === "CONFIRMED").length;
  const completedCount = filteredAppointments.filter((appointment) => String(appointment.status || "").toUpperCase() === "COMPLETED").length;
  const cancelledCount = filteredAppointments.filter((appointment) => ["CANCELLED", "CANCELED"].includes(String(appointment.status || "").toUpperCase())).length;
  const calendarDays = useMemo(() => calendarDaysFor(calendarMonth), [calendarMonth]);
  const selectedDateAppointments = useMemo(
    () => filteredAppointments.filter((appointment) => overlapsDay(appointment, parseDateKey(selectedCalendarDate))),
    [filteredAppointments, selectedCalendarDate],
  );
  const workspaceMemberOptions = useMemo(
    () => workspaceUsers.map((user) => {
      const name = getWorkspaceMemberDisplayName(user);
      const email = getWorkspaceMemberEmail(user);
      const label = email ? `${name} (${email})` : name;
      return {
        value: user.id,
        label,
        name,
        email,
      };
    }),
    [workspaceUsers],
  );

  const pickCalendarDate = (date) => {
    setSelectedCalendarDate(localDateKey(date));
  };
  const openAppointmentDetails = (appointment) => {
    setCalendarFullscreenOpen(false);
    setCalendarMoreDay(null);
    setSelectedAppointment(appointment);
  };
  const openCalendarMoreDay = (date, dayAppointments = []) => {
    setSelectedCalendarDate(localDateKey(date));
    setCalendarMoreDay({
      label: date.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" }),
      appointments: [...dayAppointments].sort((left, right) => new Date(appointmentStartValue(left)) - new Date(appointmentStartValue(right))),
    });
  };
  const openNewAppointment = (date = selectedCalendarDate) => {
    const dateKey = dateKeyFor(date);
    if (isPastDateKey(dateKey)) {
      message.warning("Appointments can only be added for today or a future date.");
      return;
    }
    setCalendarFullscreenOpen(false);
    setCalendarMoreDay(null);
    setEditingAppointment(null);
    setAppointmentForm(defaultAppointmentForm(dateKey));
    setAppointmentFormError("");
    setAppointmentEditorOpen(true);
  };
  const openUpdateAppointment = (appointment) => {
    if (!appointment?.id) return;
    setCalendarFullscreenOpen(false);
    setCalendarMoreDay(null);
    setSelectedAppointment(null);
    setEditingAppointment(appointment);
    setAppointmentForm(appointmentFormFrom(appointment));
    setAppointmentFormError("");
    setAppointmentEditorOpen(true);
  };
  const closeAppointmentEditor = () => {
    if (appointmentSaving) return;
    setAppointmentEditorOpen(false);
    setEditingAppointment(null);
    setAppointmentForm(defaultAppointmentForm(selectedCalendarDate));
    setAppointmentFormError("");
  };
  const updateAppointmentForm = (field, value) => {
    setAppointmentFormError("");
    setAppointmentForm((current) => {
      if (field === "date") {
        const dateKey = isPastDateKey(value) ? today() : value;
        const start = futureStartFor(dateKey);
        const nextStartTime = start.date === today() && current.startTime < start.time ? start.time : current.startTime;
        return {
          ...current,
          date: start.date,
          startTime: nextStartTime,
          endTime: current.endTime > nextStartTime ? current.endTime : addMinutesToTime(nextStartTime, 30),
        };
      }
      if (field === "startTime") {
        return { ...current, startTime: value, endTime: current.endTime || addMinutesToTime(value, 30) };
      }
      return { ...current, [field]: value };
    });
  };
  const updateAppointmentHost = (hostUserId) => {
    setAppointmentFormError("");
    const host = workspaceMemberOptions.find((option) => option.value === hostUserId);
    setAppointmentForm((current) => ({
      ...current,
      hostUserId: host?.value || "",
      hostName: host?.name || "",
      hostEmail: host?.email || "",
    }));
  };
  const saveAppointment = async () => {
    const startTimestamp = zonedIsoFromDateAndTime(appointmentForm.date, appointmentForm.startTime);
    const endTimestamp = zonedIsoFromDateAndTime(appointmentForm.date, appointmentForm.endTime);
    const startDate = new Date(startTimestamp || "");
    const endDate = new Date(endTimestamp || "");
    if (!appointmentForm.customerName.trim()) {
      setAppointmentFormError("Customer name is required.");
      return;
    }
    if (!startTimestamp || !endTimestamp || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      setAppointmentFormError("Choose an appointment date, start time, and end time.");
      return;
    }
    if (startDate < new Date()) {
      setAppointmentFormError("Appointment start time must be now or in the future.");
      return;
    }
    if (endDate <= startDate) {
      setAppointmentFormError("End time must be after start time.");
      return;
    }
    setAppointmentSaving(true);
    setAppointmentFormError("");
    try {
      const payload = {
        workspaceId: selectedWorkspaceId,
        vehicleId: appointmentForm.vehicleId.trim() || null,
        customerInquiryId: editingAppointment?.customerInquiryId || editingAppointment?.inquiryId || null,
        reference: editingAppointment?.reference || null,
        customerName: appointmentForm.customerName.trim(),
        customerEmail: appointmentForm.customerEmail.trim(),
        customerPhone: appointmentForm.customerPhone.trim(),
        title: appointmentForm.title.trim() || `Visit with ${appointmentForm.customerName.trim()}`,
        location: appointmentForm.location.trim(),
        notes: appointmentForm.notes.trim(),
        host: {
          id: appointmentForm.hostUserId || null,
          name: appointmentForm.hostName.trim(),
          email: appointmentForm.hostEmail.trim(),
        },
        startTimestamp,
        endTimestamp,
        status: appointmentForm.status,
      };
      const saved = editingAppointment?.id
        ? await careService.appointments.update(editingAppointment.id, payload)
        : await careService.appointments.create(payload);
      const normalized = normalizeAppointment(saved || { ...editingAppointment, ...payload });
      message.success(editingAppointment?.id ? "Appointment updated." : "Appointment added.");
      setAppointmentEditorOpen(false);
      setEditingAppointment(null);
      setAppointmentForm(defaultAppointmentForm(selectedCalendarDate));
      const savedDateKey = localDateKey(new Date(saved?.startTimestamp || startTimestamp));
      setSelectedCalendarDate(savedDateKey);
      if (viewMode === "calendar") {
        setCalendarMonth(parseDateKey(savedDateKey));
      }
      setAppointments((current) => {
        if (!editingAppointment?.id) return current;
        return current.map((appointment) => (appointment.id === normalized.id ? normalized : appointment));
      });
      await loadData();
    } catch (saveError) {
      setAppointmentFormError(saveError?.response?.data?.message || saveError?.message || "Unable to save appointment.");
    } finally {
      setAppointmentSaving(false);
    }
  };
  const appointmentPayloadFrom = (appointment = {}, patch = {}) => ({
    workspaceId: appointment.workspaceId || selectedWorkspaceId,
    vehicleId: appointment.vehicleId || null,
    customerInquiryId: appointment.customerInquiryId || appointment.inquiryId || null,
    reference: appointment.reference || null,
    host: {
      id: appointment.host?.id || appointment.hostId || null,
      name: clean(appointment.host?.name || appointment.hostName),
      email: clean(appointment.host?.email || appointment.hostEmail),
    },
    customerName: customerNameFor(appointment),
    customerEmail: customerEmailFor(appointment),
    customerPhone: clean(appointment.customerPhone || appointment.phone),
    customerZipCode: clean(appointment.customerZipCode || appointment.zipCode),
    customerMessage: clean(appointment.customerMessage),
    title: appointmentTitleFor(appointment),
    location: clean(appointment.location),
    notes: clean(appointment.notes),
    startTimestamp: appointmentStartValue(appointment),
    endTimestamp: appointmentEndValue(appointment),
    status: appointment.status || "SCHEDULED",
    ...patch,
  });
  const updateSelectedAppointmentStatus = async (nextStatus, successMessage) => {
    if (!selectedAppointment?.id || appointmentActionLoading) return;
    setAppointmentActionLoading(nextStatus);
    try {
      const updated = await careService.appointments.update(
        selectedAppointment.id,
        appointmentPayloadFrom(selectedAppointment, { status: nextStatus }),
      );
      const normalized = normalizeAppointment(updated || { ...selectedAppointment, status: nextStatus });
      setSelectedAppointment(normalized);
      setAppointments((current) => current.map((appointment) => (
        appointment.id === normalized.id ? normalized : appointment
      )));
      message.success(successMessage);
      await loadData();
    } catch (actionError) {
      message.error(actionError?.response?.data?.message || actionError?.message || "Unable to update appointment.");
    } finally {
      setAppointmentActionLoading("");
    }
  };
  const confirmAppointmentStatus = (nextStatus, title, successMessage) => {
    if (!selectedAppointment?.id) return;
    Modal.confirm({
      title,
      content: `${appointmentTitleFor(selectedAppointment)} for ${customerNameFor(selectedAppointment)} will be updated.`,
      okText: labelFor(nextStatus),
      cancelText: "Close",
      centered: true,
      onOk: () => updateSelectedAppointmentStatus(nextStatus, successMessage),
    });
  };
  const deleteSelectedAppointment = () => {
    if (!selectedAppointment?.id || appointmentActionLoading) return;
    Modal.confirm({
      title: "Delete appointment?",
      content: `${appointmentTitleFor(selectedAppointment)} for ${customerNameFor(selectedAppointment)} will be removed from the schedule.`,
      okText: "Delete",
      cancelText: "Close",
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        setAppointmentActionLoading("DELETE");
        try {
          await careService.appointments.delete(selectedAppointment.id);
          setAppointments((current) => current.filter((appointment) => appointment.id !== selectedAppointment.id));
          setSelectedAppointment(null);
          message.success("Appointment deleted.");
          await loadData();
        } catch (actionError) {
          message.error(actionError?.response?.data?.message || actionError?.message || "Unable to delete appointment.");
        } finally {
          setAppointmentActionLoading("");
        }
      },
    });
  };
  const closeDetailDrawer = () => {
    setDetailDrawer({ type: "", data: null, loading: false, error: "" });
  };
  const openInquiryDrawer = async (inquiryId) => {
    if (!inquiryId) return;
    setDetailDrawer({ type: "inquiry", data: null, loading: true, error: "" });
    try {
      const inquiry = await careService.customerInquiries.get(inquiryId);
      setDetailDrawer({ type: "inquiry", data: inquiry, loading: false, error: "" });
    } catch (drawerError) {
      setDetailDrawer({
        type: "inquiry",
        data: null,
        loading: false,
        error: drawerError?.response?.data?.message || drawerError?.message || "Unable to load inquiry.",
      });
    }
  };
  const openVehicleDrawer = async (vehicleId) => {
    if (!vehicleId) return;
    setDetailDrawer({ type: "vehicle", data: null, loading: true, error: "" });
    try {
      const vehicle = await careService.vehicles.get(vehicleId);
      setDetailDrawer({ type: "vehicle", data: vehicle, loading: false, error: "" });
    } catch (drawerError) {
      setDetailDrawer({
        type: "vehicle",
        data: null,
        loading: false,
        error: drawerError?.response?.data?.message || drawerError?.message || "Unable to load vehicle.",
      });
    }
  };

  const renderCalendarGrid = (mode = "inline") => {
    const isFullscreen = mode === "fullscreen";
    const visibleAppointmentLimit = 2;
    return (
      <>
        <div className={`schedule-calendar${isFullscreen ? " is-fullscreen" : ""}`}>
          {weekdayLabels.map((day) => <div className="schedule-calendar-weekday" key={day}>{day}</div>)}
          {calendarDays.map((date) => {
            const dateKey = localDateKey(date);
            const dayAppointments = filteredAppointments
              .filter((appointment) => overlapsDay(appointment, date))
              .sort((left, right) => new Date(appointmentStartValue(left)) - new Date(appointmentStartValue(right)));
            const isCurrentMonth = date.getMonth() === calendarMonth.getMonth();
            const isSelected = selectedCalendarDate === dateKey;
            const canAddOnDate = !isPastDateKey(dateKey);
            return (
              <article className={`schedule-calendar-day${isCurrentMonth ? "" : " is-muted"}${isSelected ? " is-selected" : ""}${sameDay(date, new Date()) ? " is-today" : ""}`} key={dateKey}>
                <button className="schedule-calendar-date" type="button" onClick={() => pickCalendarDate(date)}>
                  <span>{date.getDate()}</span>
                  {dayAppointments.length ? <small>{dayAppointments.length}</small> : null}
                </button>
                <div className="schedule-calendar-events">
                  {dayAppointments.slice(0, visibleAppointmentLimit).map((appointment) => (
                    <button className={`schedule-calendar-event is-appointment status-${statusClassFor(appointment.status)}`} key={appointment.id} type="button" onClick={() => openAppointmentDetails(appointment)}>
                      <span className="schedule-calendar-event-time">
                        <i className={`ti ${statusIconFor(appointment.status)}`} title={labelFor(appointment.status)} />
                        {timeRangeLabel(appointment)}
                      </span>
                      <strong>{customerNameFor(appointment)}</strong>
                    </button>
                  ))}
                  {dayAppointments.length > visibleAppointmentLimit ? (
                    <button className="schedule-calendar-more" type="button" onClick={() => openCalendarMoreDay(date, dayAppointments)}>
                      +{dayAppointments.length - visibleAppointmentLimit} more
                    </button>
                  ) : null}
                </div>
                <button
                  className="schedule-calendar-add"
                  type="button"
                  onClick={() => openNewAppointment(date)}
                  disabled={!canAddOnDate}
                  title={canAddOnDate ? "Add appointment on this date" : "Past dates cannot accept new appointments"}
                >
                  <i className="ti ti-plus" />
                </button>
              </article>
            );
          })}
        </div>
        <div className="schedule-calendar-selected">
          <div>
            <span>Selected date</span>
            <strong>{parseDateKey(selectedCalendarDate).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}</strong>
          </div>
          <div>
            <span>Appointments</span>
            <strong>{selectedDateAppointments.length}</strong>
          </div>
        </div>
      </>
    );
  };

  return (
    <CareShell className="dashboard-content vehicles-page care-staff-schedules-page">
      <section className="schedule-command-center">
        <div className="schedule-command-head">
          <div className="page-title">
            <span className="schedule-page-kicker"><i className="ti ti-calendar-clock" /> Appointments</span>
            <h1>Appointments</h1>
            <p>Track scheduled customer visits and test drives.</p>
          </div>
          <div className="care-staff-page-actions">
            <button
              className="btn primary schedule-primary-action"
              type="button"
              onClick={() => openNewAppointment(selectedCalendarDate)}
              disabled={isPastDateKey(selectedCalendarDate)}
            >
              <i className="ti ti-calendar-plus" /> Add appointment
            </button>
            <button className="mini-btn schedule-refresh-action" type="button" onClick={loadData}>
              <i className="ti ti-refresh" /> Refresh
            </button>
          </div>
        </div>

        <div className="schedule-status-strip">
          <article className="schedule-status-card is-total">
            <span><i className="ti ti-calendar-stats" /> Total</span>
            <strong>{totalCount}</strong>
            <small>{scheduledCount} scheduled · {confirmedCount} confirmed</small>
          </article>
          <article className="schedule-status-card is-active">
            <span><i className="ti ti-clock" /> Scheduled</span>
            <strong>{scheduledCount}</strong>
            <small>Upcoming visits</small>
          </article>
          <article className="schedule-status-card is-completed">
            <span><i className="ti ti-circle-check" /> Completed</span>
            <strong>{completedCount}</strong>
            <small>Finished visits</small>
          </article>
          <article className="schedule-status-card is-cancelled">
            <span><i className="ti ti-ban" /> Cancelled</span>
            <strong>{cancelledCount}</strong>
            <small>Kept for history</small>
          </article>
        </div>
      </section>

      {loading ? <Panel title="Appointments"><p>Loading appointments...</p></Panel> : null}
      {error ? <Panel title="Appointments"><p className="form-error">{error}</p></Panel> : null}

      <section className="timesheet-filter-bar schedule-control-panel">
        <label>
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="ALL">All statuses</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </label>
        <div className="schedule-view-toggle" role="group" aria-label="Appointment view">
          <button className={viewMode === "calendar" ? "is-active" : ""} type="button" onClick={() => setViewMode("calendar")}>
            <i className="ti ti-calendar-month" /> Calendar
          </button>
          <button className={viewMode === "board" ? "is-active" : ""} type="button" onClick={() => setViewMode("board")}>
            <i className="ti ti-layout-grid" /> Board
          </button>
        </div>
      </section>

      {viewMode === "calendar" ? (
        <Panel title="Appointments Calendar">
          <div className="schedule-calendar-toolbar">
            <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, -1))}>
              <i className="ti ti-chevron-left" /> Previous
            </button>
            <strong>{monthLabel(calendarMonth)}</strong>
            <div className="schedule-calendar-toolbar-actions">
              <button className="mini-btn" type="button" onClick={() => setCalendarFullscreenOpen(true)}>
                <i className="ti ti-maximize" /> Full screen
              </button>
              <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, 1))}>
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
          {renderCalendarGrid()}
        </Panel>
      ) : (
        <>
          <section className="schedule-board-range">
            <label>
              Range start
              <input type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
            </label>
            <label>
              Range end
              <input type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
            </label>
            <button className="btn primary schedule-primary-action" type="button" onClick={() => openNewAppointment(rangeStart)} disabled={isPastDateKey(rangeStart)}>
              <i className="ti ti-calendar-plus" /> Add appointment
            </button>
          </section>
          <Panel title="Appointments Board">
            <div className="schedule-board">
              {filteredAppointments.length ? filteredAppointments.map((appointment) => (
                <article className="schedule-card is-appointment" key={appointment.id} role="button" tabIndex={0} onClick={() => openAppointmentDetails(appointment)} onKeyDown={(event) => event.key === "Enter" && openAppointmentDetails(appointment)}>
                  <div className="schedule-card-type">
                    <i className="ti ti-calendar-heart" />
                    <span>Appointment</span>
                  </div>
                  <strong>{appointmentTitleFor(appointment)}</strong>
                  <small>{customerEmailFor(appointment) || appointment.customerPhone || "No contact provided"}</small>
                  <p>{formatTimestamp(appointment.startTimestamp)} - {formatTimestamp(appointment.endTimestamp)}</p>
                  {appointment.location ? <em>{appointment.location}</em> : null}
                  <div className="schedule-card-foot">
                    <span className={`schedule-status is-${statusClassFor(appointment.status)}`}>
                      <i className={`ti ${statusIconFor(appointment.status)}`} />
                      {labelFor(appointment.status)}
                    </span>
                  </div>
                </article>
              )) : (
                <div className="timesheet-empty">
                  <i className="ti ti-calendar-off" />
                  <span>No appointments found for this range.</span>
                </div>
              )}
            </div>
          </Panel>
        </>
      )}

      <Modal
        className="schedule-calendar-fullscreen-modal"
        title="Appointments Calendar"
        open={calendarFullscreenOpen}
        footer={null}
        width="calc(100vw - 48px)"
        centered
        onCancel={() => setCalendarFullscreenOpen(false)}
      >
        <div className="schedule-calendar-fullscreen-shell">
          <div className="schedule-calendar-toolbar">
            <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, -1))}>
              <i className="ti ti-chevron-left" /> Previous
            </button>
            <strong>{monthLabel(calendarMonth)}</strong>
            <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, 1))}>
              Next <i className="ti ti-chevron-right" />
            </button>
          </div>
          {renderCalendarGrid("fullscreen")}
        </div>
      </Modal>

      <Modal
        className="schedule-calendar-more-modal"
        title={calendarMoreDay?.label || "Appointments"}
        open={Boolean(calendarMoreDay)}
        footer={null}
        width={520}
        centered
        onCancel={() => setCalendarMoreDay(null)}
      >
        <div className="schedule-calendar-more-list">
          {(calendarMoreDay?.appointments || []).map((appointment) => (
            <button className={`schedule-calendar-more-row status-${statusClassFor(appointment.status)}`} key={appointment.id || appointmentStartValue(appointment)} type="button" onClick={() => openAppointmentDetails(appointment)}>
              <span className="schedule-calendar-more-row__time">
                <i className={`ti ${statusIconFor(appointment.status)}`} />
                {timeRangeLabel(appointment)}
              </span>
              <strong>{customerNameFor(appointment)}</strong>
              <small>{labelFor(appointment.status || "SCHEDULED")}</small>
            </button>
          ))}
        </div>
      </Modal>

      <Drawer className="appointment-editor-drawer" width={680} title={editingAppointment ? "Update Appointment" : "Add Appointment"} open={appointmentEditorOpen} onClose={closeAppointmentEditor} destroyOnClose>
        <div className="schedule-editor appointment-editor-compact">
          {appointmentFormError ? (
            <div className="schedule-form-warning" role="alert">
              <i className="ti ti-alert-triangle" />
              <span>{appointmentFormError}</span>
            </div>
          ) : null}
          <div className="appointment-editor-grid">
            <label>
              Customer
              <input value={appointmentForm.customerName} onChange={(event) => updateAppointmentForm("customerName", event.target.value)} placeholder="Customer name" />
            </label>
            <label>
              Status
              <select value={appointmentForm.status} onChange={(event) => updateAppointmentForm("status", event.target.value)}>
                <option value="SCHEDULED">Scheduled</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </label>
          </div>
          <div className="appointment-editor-grid">
            <label>
              Email
              <input type="email" value={appointmentForm.customerEmail} onChange={(event) => updateAppointmentForm("customerEmail", event.target.value)} placeholder="customer@example.com" />
            </label>
            <label>
              Phone
              <input value={appointmentForm.customerPhone} onChange={(event) => updateAppointmentForm("customerPhone", event.target.value)} placeholder="+1 555-555-5555" />
            </label>
          </div>
          <div className="appointment-editor-time-grid">
            <label>
              Date
              <input type="date" min={today()} value={appointmentForm.date} onChange={(event) => updateAppointmentForm("date", event.target.value)} />
            </label>
            <label>
              Start
              <input type="time" min={appointmentForm.date === today() ? futureStartFor(today()).time : undefined} value={appointmentForm.startTime} onChange={(event) => updateAppointmentForm("startTime", event.target.value)} />
            </label>
            <label>
              End
              <input type="time" value={appointmentForm.endTime} onChange={(event) => updateAppointmentForm("endTime", event.target.value)} />
            </label>
          </div>
          <div className="appointment-editor-grid">
            <label>
              Vehicle ID
              <input value={appointmentForm.vehicleId} onChange={(event) => updateAppointmentForm("vehicleId", event.target.value)} placeholder="Optional" />
            </label>
            <label>
              Host
              <Select
                allowClear
                showSearch
                loading={workspaceMembersLoading}
                optionFilterProp="label"
                placeholder="Search workspace members"
                value={appointmentForm.hostUserId || undefined}
                onChange={updateAppointmentHost}
                filterOption={(input, option) =>
                  String(option?.label || "").toLowerCase().includes(input.toLowerCase())
                }
                options={workspaceMemberOptions}
              />
            </label>
          </div>
          <label>
            Location
            <input value={appointmentForm.location} onChange={(event) => updateAppointmentForm("location", event.target.value)} placeholder="Location" />
          </label>
          <label>
            Purpose
            <input value={appointmentForm.title} onChange={(event) => updateAppointmentForm("title", event.target.value)} placeholder="Test drive, appraisal, paperwork" />
          </label>
          <label>
            Notes
            <textarea value={appointmentForm.notes} onChange={(event) => updateAppointmentForm("notes", event.target.value)} placeholder="Appointment notes" rows={2} />
          </label>
          <div className="drawer-actions">
            <button className="mini-btn" type="button" onClick={closeAppointmentEditor}>Close</button>
            <button className="btn primary" type="button" onClick={saveAppointment} disabled={appointmentSaving}>
              <i className="ti ti-device-floppy" /> {appointmentSaving ? "Saving..." : editingAppointment ? "Update appointment" : "Save appointment"}
            </button>
          </div>
        </div>
      </Drawer>

      <Drawer className="appointment-details-drawer" width={680} title="Appointment Details" open={Boolean(selectedAppointment)} onClose={() => setSelectedAppointment(null)} destroyOnClose>
        {selectedAppointment ? (
          <div className="schedule-details-drawer">
            <div className="drawer-actions appointment-detail-actions">
              <button
                className="mini-btn"
                type="button"
                onClick={() => openUpdateAppointment(selectedAppointment)}
                disabled={Boolean(appointmentActionLoading)}
              >
                <i className="ti ti-edit" /> Update
              </button>
              {canConfirmAppointment(selectedAppointment) ? (
                <button
                  className="mini-btn"
                  type="button"
                  onClick={() => confirmAppointmentStatus("CONFIRMED", "Confirm appointment?", "Appointment confirmed.")}
                  disabled={Boolean(appointmentActionLoading)}
                >
                  <i className="ti ti-circle-check" /> {appointmentActionLoading === "CONFIRMED" ? "Confirming..." : "Confirm"}
                </button>
              ) : null}
              {canCompleteAppointment(selectedAppointment) ? (
                <button
                  className="mini-btn"
                  type="button"
                  onClick={() => confirmAppointmentStatus("COMPLETED", "Complete appointment?", "Appointment completed.")}
                  disabled={Boolean(appointmentActionLoading)}
                >
                  <i className="ti ti-checks" /> {appointmentActionLoading === "COMPLETED" ? "Completing..." : "Complete"}
                </button>
              ) : null}
              {canCancelAppointment(selectedAppointment) ? (
                <button
                  className="mini-btn warning"
                  type="button"
                  onClick={() => confirmAppointmentStatus("CANCELLED", "Cancel appointment?", "Appointment cancelled.")}
                  disabled={Boolean(appointmentActionLoading)}
                >
                  <i className="ti ti-ban" /> {appointmentActionLoading === "CANCELLED" ? "Cancelling..." : "Cancel"}
                </button>
              ) : null}
              <button
                className="mini-btn danger"
                type="button"
                onClick={deleteSelectedAppointment}
                disabled={Boolean(appointmentActionLoading)}
              >
                <i className="ti ti-trash" /> {appointmentActionLoading === "DELETE" ? "Deleting..." : "Delete"}
              </button>
            </div>
            <section>
              <span>Customer</span>
              <strong>{customerNameFor(selectedAppointment)}</strong>
              <small>{customerEmailFor(selectedAppointment) || selectedAppointment.customerPhone || "No contact provided"}</small>
            </section>
            <section className="schedule-editor-grid">
              <div>
                <span>Status</span>
                <strong>{labelFor(selectedAppointment.status)}</strong>
              </div>
              <div>
                <span>Host</span>
                <strong>{selectedAppointment.host?.name || selectedAppointment.hostName || "Not assigned"}</strong>
              </div>
            </section>
            <section>
              <span>Starts</span>
              <strong>{formatTimestamp(selectedAppointment.startTimestamp)}</strong>
            </section>
            <section>
              <span>Ends</span>
              <strong>{formatTimestamp(selectedAppointment.endTimestamp)}</strong>
            </section>
            {selectedAppointment.location ? (
              <section>
                <span>Location</span>
                <p>{selectedAppointment.location}</p>
              </section>
            ) : null}
            <section className="schedule-editor-grid">
              <div>
                <span>Inquiry</span>
                <div className="schedule-linked-actions">
                  {selectedAppointment.inquiryId ? (
                    <>
                      <button className="mini-btn" type="button" onClick={() => openInquiryDrawer(selectedAppointment.inquiryId)}>
                        <i className="ti ti-message-circle" /> View inquiry
                      </button>
                      <Link className="mini-btn" to={`/customer-inquiries?inquiryId=${encodeURIComponent(selectedAppointment.inquiryId)}`}>
                        <i className="ti ti-external-link" /> Open full page
                      </Link>
                    </>
                  ) : "Not linked"}
                </div>
              </div>
              <div>
                <span>Vehicle</span>
                <div className="schedule-linked-actions">
                  {selectedAppointment.vehicleId ? (
                    <>
                      <button className="mini-btn" type="button" onClick={() => openVehicleDrawer(selectedAppointment.vehicleId)}>
                        <i className="ti ti-car" /> View vehicle
                      </button>
                      <Link className="mini-btn" to={`/vehicles/${encodeURIComponent(selectedAppointment.vehicleId)}`}>
                        <i className="ti ti-external-link" /> Open full page
                      </Link>
                    </>
                  ) : "Not linked"}
                </div>
              </div>
            </section>
            {selectedAppointment.notes ? (
              <section>
                <span>Notes</span>
                <p>{selectedAppointment.notes}</p>
              </section>
            ) : null}
          </div>
        ) : null}
      </Drawer>

      <Drawer
        className="appointment-linked-drawer"
        width={560}
        title={detailDrawer.type === "vehicle" ? "Vehicle" : "Customer Inquiry"}
        open={Boolean(detailDrawer.type)}
        onClose={closeDetailDrawer}
        destroyOnClose
      >
        {detailDrawer.loading ? <p className="vehicle-inline-empty">Loading...</p> : null}
        {detailDrawer.error ? <p className="form-error">{detailDrawer.error}</p> : null}
        {!detailDrawer.loading && !detailDrawer.error && detailDrawer.type === "inquiry" && detailDrawer.data ? (
          <div className="schedule-details-drawer">
            <section>
              <span>Customer</span>
              <strong>{customerNameFor(detailDrawer.data)}</strong>
              <small>{customerEmailFor(detailDrawer.data) || detailDrawer.data.phone || "No contact provided"}</small>
            </section>
            <section className="schedule-editor-grid">
              <div>
                <span>Status</span>
                <strong>{labelFor(detailDrawer.data.status || "NEW")}</strong>
              </div>
              <div>
                <span>ZIP</span>
                <strong>{detailDrawer.data.zipCode || "Not provided"}</strong>
              </div>
            </section>
            {detailDrawer.data.scheduledVisitAt ? (
              <section>
                <span>Scheduled visit</span>
                <strong>{formatTimestamp(detailDrawer.data.scheduledVisitAt)}</strong>
              </section>
            ) : null}
            {detailDrawer.data.messageToSeller ? (
              <section>
                <span>Message</span>
                <p>{detailDrawer.data.messageToSeller}</p>
              </section>
            ) : null}
          </div>
        ) : null}
        {!detailDrawer.loading && !detailDrawer.error && detailDrawer.type === "vehicle" && detailDrawer.data ? (
          <div className="schedule-details-drawer">
            <section>
              <span>Vehicle</span>
              <strong>{vehicleTitleFor(detailDrawer.data)}</strong>
              <small>{detailDrawer.data.status || "Status not set"}</small>
            </section>
            <section className="schedule-editor-grid">
              <div>
                <span>Price</span>
                <strong>{detailDrawer.data.price || "Not set"}</strong>
              </div>
              <div>
                <span>Mileage</span>
                <strong>{detailDrawer.data.mileage || "Not set"}</strong>
              </div>
            </section>
            <section className="schedule-editor-grid">
              <div>
                <span>Drivetrain</span>
                <strong>{detailDrawer.data.drivetrain || "Not set"}</strong>
              </div>
              <div>
                <span>Fuel</span>
                <strong>{detailDrawer.data.fuelType || "Not set"}</strong>
              </div>
            </section>
            {detailDrawer.data.description ? (
              <section>
                <span>Description</span>
                <p>{detailDrawer.data.description}</p>
              </section>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
